import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Link, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export function ImageChangeDialog({ open, onOpenChange, exerciseName, onSave }) {
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede superar 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setPreview(evt.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUrlSubmit = () => {
    if (!url.trim()) return;
    setPreview(url.trim());
  };

  const handleSave = () => {
    if (!preview) return;
    onSave(preview);
    onOpenChange(false);
    setPreview(null);
    setUrl("");
    toast.success("Imagen actualizada");
  };

  const handleClose = (open) => {
    if (!open) {
      setPreview(null);
      setUrl("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="glass-surface rounded-3xl border-0 max-w-sm p-0 overflow-hidden">
        <div className="p-6 pb-0">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground text-lg font-bold tracking-tight">
              Cambiar imagen
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {exerciseName}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 pt-4">
          <Tabs defaultValue="upload" className="w-full">
            <TabsList className="w-full bg-secondary/30 rounded-xl h-10 p-1">
              <TabsTrigger
                value="upload"
                className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Subir
              </TabsTrigger>
              <TabsTrigger
                value="url"
                className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
              >
                <Link className="h-3.5 w-3.5 mr-1.5" />
                URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="mt-4">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                variant="ghost"
                onClick={() => fileRef.current?.click()}
                className="w-full h-24 rounded-2xl border-2 border-dashed border-border/60 hover:border-primary/40 hover:bg-primary/5 flex flex-col gap-2 text-muted-foreground"
              >
                <ImageIcon className="h-6 w-6" />
                <span className="text-xs font-medium">Selecciona una imagen</span>
              </Button>
            </TabsContent>

            <TabsContent value="url" className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
                  URL de imagen
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://..."
                    className="h-10 rounded-xl bg-secondary/30 border-border/40 text-foreground text-sm font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
                  />
                  <Button
                    onClick={handleUrlSubmit}
                    disabled={!url.trim()}
                    className="h-10 rounded-xl bg-primary text-primary-foreground px-4 text-xs font-bold hover:bg-primary/90 disabled:opacity-40"
                  >
                    Cargar
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Preview */}
          {preview && (
            <div className="mt-4 rounded-2xl overflow-hidden border border-border/40">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-32 object-cover"
                onError={() => {
                  toast.error("No se pudo cargar la imagen");
                  setPreview(null);
                }}
              />
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={!preview}
            className="w-full h-11 mt-4 rounded-2xl bg-primary text-primary-foreground font-bold text-sm fp-glow hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none transition-colors duration-200"
          >
            Guardar imagen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
